import { useState, useEffect, useRef } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

const MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const PROFESSIONS = [
    "Accommodation & Food Services",
    "Accounting & Tax Services",
    "Ambulatory / Outpatient Care Services",
    "Behavioral Health & Substance Abuse Services",
    "Business & Management Services",
    "Construction",
    "Educational Services",
    "Engineering & Architecture",
    "Finance & Banking",
    "Home Healthcare Services",
    "Hospitals",
    "Information Technology & Software",
    "Insurance",
    "Legal Services",
    "Manufacturing",
    "Marketing, Advertising & Creative Services",
    "Nursing & Residential Care Facilities",
    "Other Health Practitioners",
    "Real Estate & Property Management",
    "Retail Trade",
    "Scientific Research & Technical Testing",
    "Social Assistance (Non-Medical)",
    "Transportation",
    "Warehousing & Logistics",
    "Other Industry",
];

function loadGoogleMapsScript(apiKey) {
    return new Promise((resolve, reject) => {
        if (window.google && window.google.maps && window.google.maps.places) {
            resolve();
            return;
        }
        if (document.getElementById('google-maps-script')) {
            const existing = document.getElementById('google-maps-script');
            existing.addEventListener('load', resolve);
            existing.addEventListener('error', reject);
            return;
        }
        const script = document.createElement('script');
        script.id = 'google-maps-script';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });
}

function RainCardSection({ kycStatus, walletBalance }) {
    const balanceUsd = walletBalance ?? 0; // walletBalance is already USDC = USD
    const [status, setStatus]       = useState(null);  // null=loading, 'none', 'pending', 'approved', 'error'
    const [card, setCard]           = useState(null);   // { last4, expiry_month, expiry_year, card_status, kyc_url, application_status }
    const [applying, setApplying]   = useState(false);
    const [repayAmt, setRepayAmt]   = useState('');
    const [repaying, setRepaying]   = useState(false);
    const [repayMsg, setRepayMsg]   = useState('');
    const [applyErr, setApplyErr]   = useState('');

    useEffect(() => {
        if (kycStatus !== 'Approved') { setStatus('kyc_required'); return; }
        api.get('/wallet/rain-card').then(res => {
            if (res.data.none) { setStatus('none'); return; }
            const rc = res.data;
            setCard(rc);
            setStatus(rc.application_status === 'approved' ? 'approved' : 'pending');
        }).catch(() => setStatus('error'));
    }, [kycStatus]);

    const handleApply = async () => {
        setApplying(true);
        setApplyErr('');
        try {
            const res = await api.post('/wallet/rain-card/apply');
            const rc = res.data;
            if (rc.alreadyApplied) {
                setCard(rc.card);
                setStatus(rc.card?.application_status === 'approved' ? 'approved' : 'pending');
            } else {
                // Fresh application — open KYC link if provided
                setCard({ application_status: rc.applicationStatus, kyc_url: rc.kyc_url });
                setStatus('pending');
                if (rc.kyc_url) window.open(rc.kyc_url, '_blank', 'noopener');
            }
        } catch (err) {
            setApplyErr(err.response?.data?.error || 'Application failed. Please try again.');
        } finally {
            setApplying(false);
        }
    };

    const handleRepay = async () => {
        if (!repayAmt || parseFloat(repayAmt) <= 0) return;
        setRepaying(true);
        setRepayMsg('');
        try {
            const res = await api.post('/wallet/rain-card/repay', { amount_usd: parseFloat(repayAmt) });
            setRepayMsg(`Payment sent! Tx: ${res.data.txHash?.slice(0, 16)}…`);
            setRepayAmt('');
        } catch (err) {
            setRepayMsg(err.response?.data?.error || 'Payment failed. Check your wallet balance.');
        } finally {
            setRepaying(false);
        }
    };

    const cardBoxStyle = {
        background: 'rgba(99,91,255,0.08)',
        border: '1px solid rgba(99,91,255,0.2)',
        borderRadius: '14px',
        padding: '16px 18px',
    };

    const btnStyle = (disabled) => ({
        padding: '10px 22px', borderRadius: '10px', border: 'none', fontWeight: 700, fontSize: '0.88rem',
        cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
        background: disabled ? 'rgba(255,255,255,0.08)' : 'linear-gradient(135deg, #635bff 0%, #a78bfa 100%)',
        color: disabled ? 'rgba(255,255,255,0.3)' : '#ffffff',
        boxShadow: disabled ? 'none' : '0 4px 16px rgba(99,91,255,0.3)',
    });

    if (status === null) {
        return <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', margin: 0 }}>Loading…</p>;
    }

    if (status === 'error') {
        return <p style={{ color: '#ef4444', fontSize: '0.85rem', margin: 0 }}>Could not load Rain card status. Please refresh.</p>;
    }

    if (status === 'kyc_required') {
        return (
            <div style={{ ...cardBoxStyle, opacity: 0.7 }}>
                <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                    Identity verification (KYC) must be approved before you can apply for a Rain Visa Card.
                </p>
            </div>
        );
    }

    if (balanceUsd < 20) {
        return (
            <div style={{ ...cardBoxStyle, opacity: 0.6 }}>
                <p style={{ margin: '0 0 6px', fontWeight: 600, fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)' }}>
                    Minimum balance required
                </p>
                <p style={{ margin: 0, color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                    You need at least <strong style={{ color: 'rgba(255,255,255,0.65)' }}>$20.00</strong> in your You Bee wallet to apply for a Rain Visa Card.
                    {walletBalance !== null
                        ? ` Your current balance is $${balanceUsd.toFixed(2)}.`
                        : ''}
                </p>
            </div>
        );
    }

    if (status === 'none') {
        return (
            <div>
                <div style={{ ...cardBoxStyle, marginBottom: '14px' }}>
                    <p style={{ margin: '0 0 4px', fontWeight: 600, fontSize: '0.9rem', color: '#ffffff' }}>How it works</p>
                    <ul style={{ margin: '6px 0 0', paddingLeft: '18px', color: 'rgba(255,255,255,0.6)', fontSize: '0.82rem', lineHeight: 1.7 }}>
                        <li>Apply in under 2 minutes — a short Rain KYC step is required</li>
                        <li>Your You Bee wallet balance acts as collateral for the card's credit limit</li>
                        <li>Spend anywhere Visa is accepted (virtual card, Apple/Google Pay)</li>
                        <li>Pay your balance from your You Bee wallet directly on this page</li>
                    </ul>
                </div>
                {applyErr && <p style={{ color: '#ef4444', fontSize: '0.83rem', margin: '0 0 10px' }}>{applyErr}</p>}
                <button onClick={handleApply} disabled={applying} style={btnStyle(applying)}>
                    {applying ? 'Applying…' : 'Apply for Rain Visa Card'}
                </button>
            </div>
        );
    }

    if (status === 'pending') {
        const appStatus = card?.application_status || 'pending';
        const label = appStatus === 'needsVerification' ? 'KYC Required'
            : appStatus === 'manualReview' ? 'Under Review'
            : 'Pending';
        return (
            <div style={cardBoxStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <span style={{
                        background: 'rgba(255,193,7,0.15)', border: '1px solid rgba(255,193,7,0.3)',
                        borderRadius: '12px', padding: '2px 10px',
                        fontSize: '0.75rem', color: '#ffc107', fontWeight: 700,
                    }}>⏳ {label}</span>
                </div>
                <p style={{ margin: '0 0 12px', color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                    {appStatus === 'needsVerification'
                        ? 'Rain requires a quick identity check before issuing your card. Click the link below to complete it.'
                        : appStatus === 'manualReview'
                        ? 'Your application is under manual review. This usually takes 1–2 business days.'
                        : 'Your Rain card application is being processed. Check back shortly.'}
                </p>
                {card?.kyc_url && (
                    <a
                        href={card.kyc_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            display: 'inline-block', padding: '10px 22px', borderRadius: '10px',
                            background: 'linear-gradient(135deg, #635bff 0%, #a78bfa 100%)',
                            color: '#ffffff', fontWeight: 700, fontSize: '0.88rem',
                            textDecoration: 'none', boxShadow: '0 4px 16px rgba(99,91,255,0.3)',
                        }}
                    >
                        Complete Rain KYC →
                    </a>
                )}
            </div>
        );
    }

    // status === 'approved' — card is active
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Card widget */}
            <div style={{
                background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4c1d95 100%)',
                border: '1px solid rgba(99,91,255,0.35)',
                borderRadius: '16px', padding: '20px 22px',
                boxShadow: '0 8px 32px rgba(99,91,255,0.2)',
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                    <div>
                        <p style={{ margin: 0, fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Rain Visa</p>
                        <p style={{ margin: '2px 0 0', fontSize: '0.85rem', color: '#a78bfa', fontWeight: 600 }}>
                            {card?.card_status === 'active' ? '● Active' : card?.card_status || 'Issuing…'}
                        </p>
                    </div>
                    <svg viewBox="0 0 38 24" width="38" height="24" style={{ opacity: 0.8 }}>
                        <rect width="38" height="24" rx="4" fill="white" opacity="0.1"/>
                        <text x="4" y="17" fill="white" fontSize="11" fontWeight="bold">VISA</text>
                    </svg>
                </div>
                <p style={{ margin: '0 0 6px', fontFamily: 'monospace', fontSize: '1.1rem', letterSpacing: '0.15em', color: '#ffffff' }}>
                    •••• •••• •••• {card?.last4 || '????'}
                </p>
                <div style={{ display: 'flex', gap: '24px' }}>
                    <div>
                        <p style={{ margin: 0, fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Expires</p>
                        <p style={{ margin: '2px 0 0', fontSize: '0.85rem', color: '#ffffff', fontWeight: 600 }}>
                            {card?.expiry_month || '??'}/{(card?.expiry_year || '????').slice(-2)}
                        </p>
                    </div>
                </div>
            </div>

            {/* Repayment */}
            <div style={cardBoxStyle}>
                <p style={{ margin: '0 0 12px', fontWeight: 600, fontSize: '0.9rem', color: '#ffffff' }}>Pay Card Balance</p>
                <p style={{ margin: '0 0 12px', color: 'rgba(255,255,255,0.55)', fontSize: '0.82rem', lineHeight: 1.5 }}>
                    Send USDC from your You Bee wallet to cover your Rain card balance.
                </p>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                        type="number"
                        min="1"
                        step="0.01"
                        placeholder="Amount (USD)"
                        value={repayAmt}
                        onChange={e => setRepayAmt(e.target.value)}
                        style={{
                            padding: '10px 14px', borderRadius: '10px',
                            border: '1px solid rgba(99,91,255,0.3)',
                            background: 'rgba(99,91,255,0.08)',
                            color: '#ffffff', fontSize: '0.9rem', width: '150px', boxSizing: 'border-box',
                            outline: 'none',
                        }}
                    />
                    <button onClick={handleRepay} disabled={repaying || !repayAmt} style={btnStyle(repaying || !repayAmt)}>
                        {repaying ? 'Sending…' : 'Pay Now'}
                    </button>
                </div>
                {repayMsg && (
                    <p style={{
                        margin: '10px 0 0', fontSize: '0.83rem',
                        color: repayMsg.startsWith('Payment sent') ? '#22c55e' : '#ef4444',
                    }}>{repayMsg}</p>
                )}
            </div>
        </div>
    );
}

function Profile() {
    const { user, updateUser } = useAuth();
    const inputRef = useRef(null);        // input element for Autocomplete
    const autocompleteRef = useRef(null); // holds the Autocomplete instance

    const [legalName, setLegalName]         = useState('');
    const [address, setAddress]             = useState('');
    const [employer, setEmployer]           = useState('');
    const [profession, setProfession]       = useState('');
    const [customProfession, setCustomProfession] = useState('');
    const [professionLocked, setProfessionLocked] = useState(false); // true once set in DB
    const [verified, setVerified]           = useState(false);
    const [autoGiveback, setAutoGiveback]   = useState(false);
    const [saved, setSaved]                 = useState(false);
    const [mapsAvailable, setMapsAvailable] = useState(true);

    // KYC state
    const [kycStatus, setKycStatus]         = useState(user?.kyc_status || null);
    const [kycDeclineReason, setKycDeclineReason] = useState(user?.kyc_decline_reason || null);
    const [kycLoading, setKycLoading]       = useState(false);
    const [kycRefreshing, setKycRefreshing] = useState(false);
    const [kycError, setKycError]           = useState('');
    const [kycTooltip, setKycTooltip]       = useState(false);
    const [walletBalance, setWalletBalance] = useState(null);
    const kycPollingRef                     = useRef(null);

    // Google account link state
    const [googleLinked, setGoogleLinked] = useState(!!user?.hasGoogle);
    const [legalNameTooltip, setLegalNameTooltip] = useState(false);
    const [linkError, setLinkError] = useState('');
    const [linking, setLinking] = useState(false);

    // Load existing profile (address/employer from localStorage, profession from backend)
    useEffect(() => {
        const stored = localStorage.getItem('profile');
        if (stored) {
            try {
                const p = JSON.parse(stored);
                setAddress(p.address || '');
                setEmployer(p.employer || '');
                if (p.verified) setVerified(true);
            } catch (e) {
                localStorage.removeItem('profile');
            }
        }

        // Fetch legal name + profession from backend
        api.get('/profile').then(res => {
            if (res.data.legal_name) setLegalName(res.data.legal_name);
            if (res.data.profession) {
                setProfession(res.data.profession);
                setProfessionLocked(!!res.data.profession_locked);
            }
            if (res.data.address) {
                setAddress(res.data.address);
                setVerified(!!res.data.address_verified);
            }
            if (res.data.employer) setEmployer(res.data.employer);
            setAutoGiveback(!!res.data.auto_giveback);
        }).catch(() => {});

        // Fetch USDC wallet balance for KYC fee gate ($1.00 minimum)
        api.get('/balance').then(res => {
            setWalletBalance(res.data.balance ?? 0);
        }).catch(() => {});
    }, []);

    // Handle Didit callback redirect: /profile?verificationSessionId=...&status=...
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get('verificationSessionId');
        const status = params.get('status');
        if (!sessionId || !status) return;
        // Clean the URL immediately
        window.history.replaceState({}, '', '/profile');
        console.log('KYC callback received:', { sessionId, status });
        api.post('/kyc/complete', { session_id: sessionId, status })
            .then(res => {
                console.log('KYC complete response:', res.data);
                setKycStatus(res.data.kyc_status);
                if (res.data.kyc_decline_reason) setKycDeclineReason(res.data.kyc_decline_reason);
            })
            .catch(err => {
                console.error('KYC complete failed:', err.response?.status, err.response?.data || err.message);
                // Status still came via URL — update it locally even if backend call failed
                setKycStatus(status);
            });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Clean up KYC polling on unmount
    useEffect(() => {
        return () => { if (kycPollingRef.current) clearInterval(kycPollingRef.current); };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Load Google Maps & attach Places Autocomplete
    useEffect(() => {
        if (!MAPS_API_KEY || !inputRef.current) return;

        loadGoogleMapsScript(MAPS_API_KEY).then(() => {
            if (autocompleteRef.current) return;

            const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
                componentRestrictions: { country: 'us' },
                types: ['address'],
                fields: ['formatted_address'],
            });

            ac.addListener('place_changed', () => {
                const place = ac.getPlace();
                const addr = place.formatted_address || inputRef.current.value;
                if (addr) {
                    setAddress(addr);
                    setVerified(true);
                }
            });

            // Inject dark theme styles for the dropdown
            if (!document.getElementById('pac-dark-styles')) {
                const style = document.createElement('style');
                style.id = 'pac-dark-styles';
                style.textContent = `
                    .pac-container {
                        background-color: #1a1a2e !important;
                        border: 1px solid rgba(255, 255, 255, 0.1) !important;
                        border-radius: 12px !important;
                        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4) !important;
                        margin-top: 4px !important;
                    }
                    .pac-item {
                        background-color: #1a1a2e !important;
                        color: #ffffff !important;
                        border-top: 1px solid rgba(255, 255, 255, 0.06) !important;
                        padding: 10px 14px !important;
                        cursor: pointer !important;
                    }
                    .pac-item:first-child { border-top: none !important; }
                    .pac-item:hover, .pac-item-selected {
                        background-color: rgba(255, 193, 7, 0.1) !important;
                    }
                    .pac-item-query { color: #ffffff !important; }
                    .pac-matched { color: #ffc107 !important; }
                    .pac-icon { filter: invert(1) !important; }
                `;
                document.head.appendChild(style);
            }

            autocompleteRef.current = ac;
        }).catch(() => {
            console.warn('Google Maps failed to load — address verification unavailable.');
            setMapsAvailable(false);
        });
    }, []);

    const handleGoogleLink = async (credentialResponse) => {
        setLinkError('');
        setLinking(true);
        try {
            // Send raw credential to backend for server-side verification
            const res = await api.post('/auth/google/link', {
                credential: credentialResponse.credential,
            });
            // Decode client-side only for picture (non-security-critical UI data)
            const decoded = jwtDecode(credentialResponse.credential);
            updateUser(res.data.user, { picture: decoded.picture });
            setGoogleLinked(true);
        } catch (err) {
            setLinkError(err.response?.data?.error || 'Failed to link Google account.');
        }
        setLinking(false);
    };

    const startKycPolling = () => {
        let attempts = 0;
        kycPollingRef.current = setInterval(async () => {
            attempts++;
            try {
                const res = await api.get('/users/me');
                const status = res.data.kyc_status;
                if (status && status !== kycStatus) {
                    setKycStatus(status);
                    setKycDeclineReason(res.data.kyc_decline_reason || null);
                    updateUser({ kyc_status: status, kyc_decline_reason: res.data.kyc_decline_reason || null });
                }
                if (status === 'Approved' || status === 'Declined' || attempts > 60) {
                    clearInterval(kycPollingRef.current);
                }
            } catch { /* ignore */ }
        }, 5000);
    };

    const handleRefreshKyc = async () => {
        setKycRefreshing(true);
        setKycError('');
        try {
            const res = await api.post('/kyc/sync');
            setKycStatus(res.data.kyc_status);
            setKycDeclineReason(res.data.kyc_decline_reason || null);
            updateUser({ kyc_status: res.data.kyc_status, kyc_decline_reason: res.data.kyc_decline_reason || null });
        } catch (err) {
            setKycError(err.response?.data?.error || 'Could not check status. Try again shortly.');
        } finally {
            setKycRefreshing(false);
        }
    };

    const handleStartKyc = async () => {
        setKycLoading(true);
        setKycError('');
        try {
            const res = await api.post('/kyc/session');
            console.log('KYC session response:', res.data);
            if (!res.data.url) {
                setKycError('Verification started but no URL returned. Raw: ' + JSON.stringify(res.data._raw));
                return;
            }
            window.location.href = res.data.url;
            startKycPolling();
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to start verification. Please try again.';
            const detail = err.response?.data?.detail;
            setKycError(detail ? `${msg} (${detail})` : msg);
        } finally {
            setKycLoading(false);
        }
    };

    // Clean up polling on unmount
    const kycPollingRefCleanup = kycPollingRef;
    // (cleanup handled inline via useEffect below)

    const handleSave = async (e) => {
        e.preventDefault();

        // Save all profile data to backend
        const professionToSave = profession === 'Other' ? customProfession.trim() : profession;
        try {
            const payload = {
                address,
                address_verified: verified,
                employer,
                auto_giveback: autoGiveback,
                ...(!user?.legal_name_confirmed && legalName ? { legal_name: legalName } : {}),
            };

            // Only include profession if not locked and user selected one
            if (!professionLocked && professionToSave) {
                payload.profession = professionToSave;
            }

            await api.put('/profile', payload);

            if (!professionLocked && professionToSave) {
                setProfession(professionToSave);
                setProfessionLocked(true);
            }

            updateUser({ ...user, address, profession: professionToSave || user?.profession, legal_name: legalName, legal_name_confirmed: true });
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (err) {
            // Handle profession lock error
            if (err.response?.status === 403) setProfessionLocked(true);
        }
    };

    // Shared input styles for dark theme
    const inputStyle = {
        width: '100%',
        padding: '14px 16px',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        background: 'rgba(255, 255, 255, 0.05)',
        color: '#ffffff',
        fontSize: '0.95rem',
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s, background 0.2s',
    };

    const handleInputFocus = (e) => {
        e.target.style.borderColor = 'rgba(255, 193, 7, 0.5)';
        e.target.style.background = 'rgba(255, 255, 255, 0.08)';
    };

    const handleInputBlur = (e) => {
        e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(180deg, #12121a 0%, #1a1a2e 50%, #12121a 100%)',
            color: '#ffffff',
            position: 'relative',
            overflow: 'hidden',
        }}>
            {/* Decorative gradient orbs */}
            <div style={{
                position: 'fixed',
                top: '-20%',
                right: '-10%',
                width: '500px',
                height: '500px',
                background: 'radial-gradient(circle, rgba(255, 193, 7, 0.12) 0%, transparent 70%)',
                borderRadius: '50%',
                pointerEvents: 'none',
                zIndex: 0,
            }} />
            <div style={{
                position: 'fixed',
                bottom: '-30%',
                left: '-10%',
                width: '400px',
                height: '400px',
                background: 'radial-gradient(circle, rgba(255, 193, 7, 0.08) 0%, transparent 70%)',
                borderRadius: '50%',
                pointerEvents: 'none',
                zIndex: 0,
            }} />

            <div style={{
                padding: '32px 24px',
                maxWidth: '600px',
                margin: '0 auto',
                position: 'relative',
                zIndex: 1,
            }}>
                {/* Page Header */}
                <div style={{ marginBottom: '32px' }}>
                    <h1 style={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        marginBottom: '8px',
                        background: 'linear-gradient(135deg, #ffffff 0%, rgba(255, 255, 255, 0.7) 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                    }}>
                        My Profile
                    </h1>
                    <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.95rem', margin: 0 }}>
                        Your profile information helps qualify you for the right You-Bee distribution pools.
                    </p>
                </div>

                <form
                    onSubmit={handleSave}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px',
                        background: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        borderRadius: '20px',
                        backdropFilter: 'blur(20px)',
                        padding: '28px',
                    }}
                >

                    {/* Legal Name — read-only, set at registration */}
                    <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, marginBottom: '8px', color: '#ffffff', fontSize: '0.9rem' }}>
                            Legal Full Name
                            {kycStatus === 'Approved' && <span style={{ fontSize: '0.75rem', color: '#22c55e', fontWeight: 500 }}>✔ Verified by KYC</span>}
                            {kycStatus !== 'Approved' && (
                                <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                                    <span
                                        onMouseEnter={() => setLegalNameTooltip(true)}
                                        onMouseLeave={() => setLegalNameTooltip(false)}
                                        style={{
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            width: '16px', height: '16px', borderRadius: '50%',
                                            background: 'rgba(255,193,7,0.2)', border: '1px solid rgba(255,193,7,0.4)',
                                            color: '#ffc107', fontSize: '0.7rem', fontWeight: 700, cursor: 'default', flexShrink: 0,
                                        }}
                                    >i</span>
                                    {legalNameTooltip && (
                                        <div style={{
                                            position: 'absolute', left: '24px', top: '50%', transform: 'translateY(-50%)',
                                            zIndex: 100, width: '280px',
                                            background: '#1a1a2e', border: '1px solid rgba(255,193,7,0.3)',
                                            borderRadius: '10px', padding: '12px 14px',
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                                            fontSize: '0.8rem', color: 'rgba(255,255,255,0.85)', fontWeight: 400, lineHeight: 1.5,
                                        }}>
                                            <strong style={{ color: '#ffc107', display: 'block', marginBottom: '4px' }}>Important</strong>
                                            Your legal full name must exactly match your government-issued ID. This will be verified before any funds can be withdrawn.
                                        </div>
                                    )}
                                </span>
                            )}
                        </label>
                        {!user?.legal_name_confirmed && kycStatus !== 'Approved' ? (
                            <>
                                <input
                                    type="text"
                                    value={legalName}
                                    onChange={e => setLegalName(e.target.value)}
                                    placeholder="First Last"
                                    style={inputStyle}
                                />
                                <div style={{
                                    margin: '8px 0 0', padding: '10px 14px',
                                    background: 'rgba(255, 193, 7, 0.08)',
                                    border: '1px solid rgba(255, 193, 7, 0.25)',
                                    borderRadius: '8px',
                                    fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.5,
                                }}>
                                    <strong style={{ color: '#ffc107' }}>One-time edit:</strong> Your name was imported from Google. Please confirm it matches your government-issued ID exactly — this cannot be changed after saving.
                                </div>
                            </>
                        ) : (
                            <>
                                <div style={{
                                    padding: '14px 16px',
                                    background: 'rgba(255, 255, 255, 0.03)',
                                    border: '1px solid rgba(255, 255, 255, 0.06)',
                                    borderRadius: '12px',
                                    fontSize: '0.95rem',
                                    color: legalName ? '#ffffff' : 'rgba(255, 255, 255, 0.5)',
                                }}>
                                    {legalName || '—'}
                                </div>
                                <small style={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', marginTop: '6px' }}>
                                    Set at registration. To update, please{' '}
                                    <a href="mailto:support@you-bee.com" style={{ color: '#ffc107', textDecoration: 'underline' }}>
                                        contact support
                                    </a>.
                                </small>
                            </>
                        )}
                    </div>

                    {/* Address — PlaceAutocompleteElement (new Places API) */}
                    <div>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', color: '#ffffff', fontSize: '0.9rem' }}>
                            Residential Address <span style={{ color: '#ef4444' }}>*</span>{kycStatus === 'Approved' && <span style={{ marginLeft: '8px', fontSize: '0.75rem', color: '#22c55e', fontWeight: 500 }}>✔ Verified by KYC</span>}
                        </label>

                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Start typing your address…"
                            defaultValue={address}
                            disabled={kycStatus === 'Approved'}
                            style={{ ...inputStyle, ...(kycStatus === 'Approved' ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            onChange={() => setVerified(false)}
                        />

                        {/* Show the currently selected / saved address (hidden after KYC since it can't be changed) */}
                        {address && kycStatus !== 'Approved' && (
                            <div style={{
                                marginTop: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '10px 14px',
                                background: 'rgba(255, 255, 255, 0.03)',
                                borderRadius: '10px',
                                border: '1px solid rgba(255, 255, 255, 0.06)',
                            }}>
                                {verified && (
                                    <span style={{ color: '#22c55e', fontSize: '1rem' }}>✔</span>
                                )}
                                <span style={{ fontSize: '0.875rem', color: '#ffffff', flex: 1 }}>{address}</span>
                                <button
                                    type="button"
                                    onClick={() => { setAddress(''); setVerified(false); }}
                                    style={{
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                        borderRadius: '6px',
                                        color: 'rgba(255, 255, 255, 0.6)',
                                        cursor: 'pointer',
                                        fontSize: '0.8rem',
                                        padding: '8px 14px',
                                        minHeight: '36px',
                                    }}
                                    title="Clear address"
                                >
                                    Clear
                                </button>
                            </div>
                        )}

                        {kycStatus !== 'Approved' && (
                            <small style={{
                                color: verified ? '#22c55e' : 'rgba(255, 255, 255, 0.5)',
                                fontWeight: verified ? 600 : 400,
                                display: 'block',
                                marginTop: '6px',
                            }}>
                                {verified ? 'Address verified by Google Maps' : 'Select a suggested address to verify it.'}
                            </small>
                        )}
                    </div>

                    {/* Employer — optional */}
                    <div>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', color: '#ffffff', fontSize: '0.9rem' }}>
                            Current / Latest Employer <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontWeight: 400 }}>(optional)</span>
                        </label>
                        <input
                            type="text"
                            value={employer}
                            onChange={e => setEmployer(e.target.value)}
                            placeholder="e.g. Acme Corp"
                            style={inputStyle}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                        />
                        <small style={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', marginTop: '6px' }}>
                            Used to match you with company-specific pools.
                        </small>
                    </div>

                    {/* Profession — set once, locked after */}
                    <div>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', color: '#ffffff', fontSize: '0.9rem' }}>
                            Profession / Industry{' '}
                            {professionLocked
                                ? <span style={{ color: '#22c55e', fontWeight: 400, fontSize: '0.82rem' }}>✔ Set</span>
                                : <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontWeight: 400 }}>(optional)</span>
                            }
                        </label>

                        {professionLocked ? (
                            <div style={{
                                padding: '14px 16px',
                                background: 'rgba(255, 255, 255, 0.03)',
                                border: '1px solid rgba(255, 255, 255, 0.06)',
                                borderRadius: '12px',
                                fontSize: '0.95rem',
                                color: '#ffffff',
                            }}>
                                {profession}
                            </div>
                        ) : (
                            <>
                            <select
                                value={profession}
                                onChange={e => { setProfession(e.target.value); setCustomProfession(''); }}
                                style={{
                                    ...inputStyle,
                                    cursor: 'pointer',
                                    appearance: 'none',
                                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ffffff' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'right 16px center',
                                    paddingRight: '40px',
                                }}
                                onFocus={handleInputFocus}
                                onBlur={handleInputBlur}
                            >
                                <option value="" style={{ background: '#1a1a2e', color: '#ffffff' }}>— Select your profession —</option>
                                {PROFESSIONS.map(p => (
                                    <option key={p} value={p} style={{ background: '#1a1a2e', color: '#ffffff' }}>{p}</option>
                                ))}
                            </select>
                            {profession === 'Other' && (
                                <input
                                    type="text"
                                    value={customProfession}
                                    onChange={e => setCustomProfession(e.target.value)}
                                    placeholder="Please describe your profession…"
                                    style={{ ...inputStyle, marginTop: '10px' }}
                                    onFocus={handleInputFocus}
                                    onBlur={handleInputBlur}
                                />
                            )}
                            </>
                        )}

                        {professionLocked ? (
                            <small style={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', marginTop: '6px' }}>
                                Your profession has been set and cannot be changed.{' '}
                                To update it, please{' '}
                                <a href="mailto:support@you-bee.com" style={{ color: '#ffc107', textDecoration: 'underline' }}>
                                    contact support
                                </a>.
                            </small>
                        ) : (
                            <small style={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', marginTop: '6px' }}>
                                Used to match you with industry pools. Once saved, this can only be changed by contacting support.
                            </small>
                        )}
                    </div>

                    {/* Auto Give Back Toggle */}
                    <div>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px', color: '#ffffff', fontSize: '0.9rem' }}>
                            Preferences
                        </label>
                        <label
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '14px',
                                cursor: 'pointer',
                                padding: '14px 16px',
                                background: 'rgba(255, 255, 255, 0.03)',
                                border: '1px solid rgba(255, 255, 255, 0.08)',
                                borderRadius: '12px',
                            }}
                        >
                            {/* Toggle switch */}
                            <div
                                onClick={() => setAutoGiveback(v => !v)}
                                style={{
                                    position: 'relative',
                                    width: '44px',
                                    height: '24px',
                                    borderRadius: '12px',
                                    background: autoGiveback ? '#ffc107' : 'rgba(255,255,255,0.15)',
                                    transition: 'background 0.2s',
                                    flexShrink: 0,
                                    cursor: 'pointer',
                                }}
                            >
                                <div style={{
                                    position: 'absolute',
                                    top: '3px',
                                    left: autoGiveback ? '23px' : '3px',
                                    width: '18px',
                                    height: '18px',
                                    borderRadius: '50%',
                                    background: '#ffffff',
                                    transition: 'left 0.2s',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                                }} />
                            </div>
                            <div>
                                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#ffffff' }}>
                                    Auto Give Back Daily UBI
                                </div>
                                <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>
                                    Automatically return your daily distribution to the community hive at 11:59 PM (based on your address).
                                </div>
                            </div>
                        </label>
                    </div>

                    <button
                        type="submit"
                        disabled={mapsAvailable && !verified}
                        title={mapsAvailable && !verified ? 'Please select a verified address first' : ''}
                        style={{
                            alignSelf: 'flex-start',
                            padding: '12px 32px',
                            borderRadius: '12px',
                            border: 'none',
                            background: (mapsAvailable && !verified)
                                ? 'rgba(255, 193, 7, 0.3)'
                                : 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                            color: '#000',
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            cursor: (mapsAvailable && !verified) ? 'not-allowed' : 'pointer',
                            boxShadow: (mapsAvailable && !verified) ? 'none' : '0 4px 20px rgba(255, 193, 7, 0.3)',
                            transition: 'all 0.2s ease',
                        }}
                    >
                        Save Profile
                    </button>

                    {mapsAvailable && !verified && (
                        <small style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                            Please select an address from the suggestions to verify before saving.
                        </small>
                    )}

                    {!mapsAvailable && (
                        <small style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                            Address verification unavailable — Google Maps could not be loaded.
                        </small>
                    )}

                    {saved && (
                        <div style={{
                            padding: '14px 18px',
                            background: 'rgba(34, 197, 94, 0.15)',
                            border: '1px solid rgba(34, 197, 94, 0.3)',
                            borderRadius: '12px',
                            color: '#22c55e',
                            fontWeight: 600,
                        }}>
                            Profile saved successfully!
                        </div>
                    )}
                </form>

                {/* Connect Google account (only shown for password-registered users) */}
                {!googleLinked && (
                    <div style={{
                        marginTop: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '14px',
                        background: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        borderRadius: '20px',
                        backdropFilter: 'blur(20px)',
                        padding: '24px',
                    }}>
                        <div>
                            <h3 style={{ margin: '0 0 4px', fontSize: '1rem', color: '#ffffff' }}>Connect Google Account</h3>
                            <p style={{ margin: 0, color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem' }}>
                                Link your Google account so you can also sign in with Google.
                            </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            <GoogleLogin
                                onSuccess={handleGoogleLink}
                                onError={() => setLinkError('Google sign-in failed. Please try again.')}
                                text="continue_with"
                                useOneTap={false}
                            />
                            {linking && <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem' }}>Linking…</span>}
                        </div>
                        {linkError && (
                            <p style={{ margin: 0, color: '#ef4444', fontSize: '0.85rem' }}>{linkError}</p>
                        )}
                    </div>
                )}

                {googleLinked && (
                    <div style={{
                        marginTop: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        background: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        borderRadius: '20px',
                        backdropFilter: 'blur(20px)',
                        padding: '20px 24px',
                    }}>
                        <span style={{ color: '#22c55e', fontSize: '1.2rem' }}>✔</span>
                        <div>
                            <p style={{ margin: 0, fontWeight: 600, fontSize: '0.9rem', color: '#ffffff' }}>Google account connected</p>
                            <p style={{ margin: 0, color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.82rem' }}>You can sign in with Google or your password.</p>
                        </div>
                    </div>
                )}

                {/* Identity Verification (KYC) */}
                <div style={{
                    marginTop: '24px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: kycStatus === 'Approved'
                        ? '1px solid rgba(34,197,94,0.3)'
                        : '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '20px',
                    backdropFilter: 'blur(20px)',
                    padding: '24px',
                }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                        <div>
                            <h3 style={{ margin: '0 0 4px', fontSize: '1rem', color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                Identity Verification
                                {kycStatus === 'Approved' && (
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                                        background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)',
                                        borderRadius: '20px', padding: '2px 10px',
                                        fontSize: '0.78rem', color: '#22c55e', fontWeight: 700,
                                    }}>✔ Verified</span>
                                )}
                                {kycStatus === 'Declined' && (
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                                        background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
                                        borderRadius: '20px', padding: '2px 10px',
                                        fontSize: '0.78rem', color: '#ef4444', fontWeight: 700,
                                    }}>✕ Declined</span>
                                )}
                                {kycStatus && kycStatus !== 'Approved' && kycStatus !== 'Declined' && (
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                                        background: 'rgba(255,193,7,0.12)', border: '1px solid rgba(255,193,7,0.3)',
                                        borderRadius: '20px', padding: '2px 10px',
                                        fontSize: '0.78rem', color: '#ffc107', fontWeight: 700,
                                    }}>⏳ Pending</span>
                                )}
                            </h3>
                            <p style={{ margin: 0, color: 'rgba(255,255,255,0.55)', fontSize: '0.875rem' }}>
                                {kycStatus === 'Approved'
                                    ? 'Your identity has been verified. You are eligible for withdrawals.'
                                    : kycStatus === 'Declined'
                                    ? (kycDeclineReason || 'Verification was declined. You may try again.')
                                    : kycStatus
                                    ? 'Verification is in progress. Click "Refresh Status" to check for updates.'
                                    : !address
                                    ? 'Please add your residential address above before starting verification.'
                                    : walletBalance !== null && walletBalance < 1
                                    ? 'You need at least $1.00 USDC in your You-Bee wallet to start verification.'
                                    : 'Required to enable withdrawals. A $1.00 fee applies. Takes about 2 minutes.'}
                            </p>
                        </div>
                        {kycStatus !== 'Approved' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                <button
                                    onClick={handleStartKyc}
                                    disabled={kycLoading || !address || !(walletBalance !== null && walletBalance >= 1)}
                                    style={{
                                        padding: '10px 20px', borderRadius: '12px', border: 'none',
                                        background: (kycLoading || !address || !(walletBalance !== null && walletBalance >= 1))
                                            ? 'rgba(255,255,255,0.08)'
                                            : 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                                        color: (kycLoading || !address || !(walletBalance !== null && walletBalance >= 1))
                                            ? 'rgba(255,255,255,0.3)'
                                            : '#000',
                                        fontWeight: 700, fontSize: '0.88rem',
                                        cursor: (kycLoading || !address || !(walletBalance !== null && walletBalance >= 1))
                                            ? 'not-allowed' : 'pointer',
                                        boxShadow: 'none',
                                        whiteSpace: 'nowrap',
                                        transition: 'all 0.2s',
                                    }}
                                >
                                    {kycLoading ? 'Processing…' : kycStatus === 'Declined' ? 'Try Again' : 'Verify Identity'}
                                </button>
                                {kycStatus && kycStatus !== 'Approved' && (
                                    <button
                                        onClick={handleRefreshKyc}
                                        disabled={kycRefreshing}
                                        style={{
                                            padding: '10px 16px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)',
                                            background: 'rgba(255,255,255,0.06)',
                                            color: kycRefreshing ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.75)',
                                            fontWeight: 600, fontSize: '0.85rem',
                                            cursor: kycRefreshing ? 'not-allowed' : 'pointer',
                                            whiteSpace: 'nowrap', transition: 'all 0.2s',
                                        }}
                                    >
                                        {kycRefreshing ? '⟳ Checking…' : '⟳ Refresh Status'}
                                    </button>
                                )}
                                <div style={{ position: 'relative', display: 'inline-flex' }}>
                                    <span
                                        onMouseEnter={() => setKycTooltip(true)}
                                        onMouseLeave={() => setKycTooltip(false)}
                                        style={{
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            width: '18px', height: '18px', borderRadius: '50%',
                                            border: '1px solid rgba(255,255,255,0.25)',
                                            color: 'rgba(255,255,255,0.45)', fontSize: '0.7rem',
                                            fontWeight: 700, cursor: 'default', userSelect: 'none', flexShrink: 0,
                                        }}
                                    >?</span>
                                    {kycTooltip && (
                                        <div style={{
                                            position: 'absolute', bottom: '26px', right: 0,
                                            background: 'rgba(20,20,32,0.97)',
                                            border: '1px solid rgba(255,193,7,0.25)',
                                            borderRadius: '10px', padding: '10px 14px',
                                            fontSize: '0.78rem', color: 'rgba(255,255,255,0.8)',
                                            whiteSpace: 'nowrap', zIndex: 100,
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                                            pointerEvents: 'none',
                                        }}>
                                            Identity verification costs <strong style={{ color: '#ffc107' }}>$1.00</strong>.<br />
                                            Your wallet needs at least $1 to proceed.
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    {kycError && (
                        <p style={{ margin: '12px 0 0', color: '#ef4444', fontSize: '0.85rem' }}>{kycError}</p>
                    )}
                    {kycStatus === 'Declined' && (
                        <p style={{ margin: '12px 0 0', color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>
                            Already re-submitted? Click "Refresh Status" to check for updates.
                        </p>
                    )}
                    {kycStatus && kycStatus !== 'Approved' && kycStatus !== 'Declined' && (
                        <p style={{ margin: '12px 0 0', color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>
                            Completed your verification? This page checks for updates every 5 seconds.
                        </p>
                    )}
                </div>

                {/* Rain Card Section */}
                <div id="rain-card" style={{
                    marginTop: '24px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '20px',
                    backdropFilter: 'blur(20px)',
                    padding: '24px',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                        <span style={{ fontSize: '1.4rem' }}>💳</span>
                        <h3 style={{ margin: 0, fontSize: '1rem', color: '#ffffff' }}>Rain Visa Card</h3>
                        <span style={{
                            fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px',
                            borderRadius: '10px', background: 'rgba(99,91,255,0.15)',
                            border: '1px solid rgba(99,91,255,0.3)', color: '#a78bfa',
                        }}>Powered by Rain</span>
                    </div>
                    <p style={{ margin: '0 0 16px', color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem', lineHeight: 1.6 }}>
                        Spend your You Bee distributions anywhere Visa is accepted with a virtual card backed by your wallet balance.
                    </p>
                    <RainCardSection kycStatus={kycStatus} walletBalance={walletBalance} />
                </div>
            </div>
        </div>
    );
}

export default Profile;
